import { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, catalogShelfDays, formatDe, labelFor, searchArticles, todayIn } from '@frisk/shared';
import { t } from '../theme';
import { useStore } from '../store';

/**
 * Putting something away by hand, without a barcode.
 *
 * This replaces the old Frisches screen. That screen was a fixed grid of
 * twenty tiles, which meant anything not on it could not be added at all —
 * typing against the whole grocery catalogue covers the same fruit and veg in
 * the same two taps and everything else besides.
 *
 * The keeping time is pre-filled from the catalogue, so adjusting it is
 * optional rather than a step.
 */
export function AddItem({ onClose }: { onClose: () => void }) {
  const createItem = useStore((s) => s.createItemManually);
  const [query, setQuery] = useState('');
  const [name, setName] = useState('');
  const [days, setDays] = useState(7);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
  }, []);

  // Local and synchronous — the catalogue is bundled, so there is no request
  // to debounce and the list can keep up with typing.
  const suggestions = useMemo(() => (name ? [] : searchArticles(query)), [query, name]);

  const today = todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const bestBefore = addDays(today, days);

  const chosen = name || query.trim();

  const pick = (articleName: string) => {
    setName(articleName);
    setQuery(articleName);
    setDays(catalogShelfDays(articleName) ?? 7);
  };

  const save = async () => {
    if (!chosen || busy) return;
    setBusy(true);
    setError(null);
    try {
      await createItem(chosen, bestBefore, qty);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Konnte nicht hinzugefügt werden');
      setBusy(false);
    }
  };

  const stepper = (
    label: string,
    value: string,
    dec: () => void,
    inc: () => void,
    hint?: string,
  ) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className="press" onClick={dec} style={stepBtn}>−</div>
        <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
          <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em' }}>{value}</div>
          {hint && <div style={{ fontFamily: t.mono, fontSize: 12, color: t.inkFaint, marginTop: 2 }}>{hint}</div>}
        </div>
        <div className="press" onClick={inc} style={stepBtn}>+</div>
      </div>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'oklch(0.12 0.01 60 / 0.72)',
        backdropFilter: 'blur(8px)',
        animation: 'fade 160ms ease-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 35,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          // Enter on a bare query takes the top suggestion — the usual case is
          // typing three letters and meaning the obvious word.
          if (!name && suggestions[0]) pick(suggestions[0].name);
          else void save();
        }}
        style={{
          width: 640,
          maxWidth: '92vw',
          borderRadius: 30,
          background: t.surface,
          border: '1px solid oklch(1 0 0 / 0.09)',
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          animation: 'sheet 200ms ease-out',
        }}
      >
        <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>
          OHNE BARCODE EINRÄUMEN
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>WAS?</span>
          <input
            ref={input}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // Typing again reopens the suggestions rather than sticking to
              // whatever was picked before.
              setName('');
            }}
            placeholder="z. B. Äpfel"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            style={{
              background: 'oklch(0.2 0.012 60)',
              border: '1px solid oklch(1 0 0 / 0.12)',
              borderRadius: 16,
              padding: '16px 18px',
              color: t.ink,
              caretColor: t.accent,
              fontSize: 24,
              fontFamily: t.sans,
              outline: 'none',
            }}
          />
        </label>

        {suggestions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {suggestions.map((a) => (
              <div
                key={a.name}
                className="press"
                onClick={() => pick(a.name)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 999,
                  background: t.surfaceSunken,
                  border: t.hairline,
                  fontSize: 18,
                  fontWeight: 500,
                }}
              >
                {a.name}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {stepper(
            'HALTBAR',
            labelFor(days),
            () => setDays((d) => Math.max(1, d - 1)),
            () => setDays((d) => Math.min(365, d + 1)),
            formatDe(bestBefore),
          )}
          {stepper(
            'ANZAHL',
            `${qty}×`,
            () => setQty((q) => Math.max(1, q - 1)),
            () => setQty((q) => Math.min(99, q + 1)),
          )}
        </div>

        {error && <div style={{ fontSize: 16, color: 'oklch(0.82 0.14 30)' }}>{error}</div>}

        <div style={{ display: 'flex', gap: 12 }}>
          <div className="press" onClick={onClose} style={{ ...bigBtn, border: t.hairlineStrong, color: t.inkMuted }}>
            Abbrechen
          </div>
          <div
            className="press"
            onClick={() => void save()}
            style={{
              ...bigBtn,
              background: chosen ? t.accent : t.control,
              color: chosen ? t.accentInk : t.inkFaint,
              flex: 1.4,
            }}
          >
            {busy ? 'Wird eingeräumt …' : 'Einräumen'}
          </div>
        </div>
      </form>
    </div>
  );
}

const stepBtn: React.CSSProperties = {
  width: 56,
  height: 56,
  flex: 'none',
  borderRadius: 16,
  background: t.control,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 28,
  fontWeight: 500,
};

const bigBtn: React.CSSProperties = {
  flex: 1,
  height: 64,
  borderRadius: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  fontWeight: 600,
};
